import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { prop, pick } from 'ramda';
import { useNavigate } from 'js/hooks/navigate';
import { setCookie } from 'js/utils/cookie.util';
import { Button } from 'js/components/common/Button';
import { storageService } from 'js/services/storage.service';
import { WebContent } from 'js/components/system/WebContent';
import { componentService } from 'js/services/component.service';
import { usePortletTranslations } from 'js/components/system/Portlet';
import { getCleanUrl, getURLParam, getBaseDomain } from 'js/utils/url.util';
import { publicEventsAPIService } from 'js/services/publicEventsAPI.service';
import { useAppSelector, useUserSelector, useCookieSettingsSelector } from 'js/store/hooks';
import { setCookiesGroups, setCommonCookieFlag } from 'js/components/main/CookieSettings/store/actions';
import { AppContext } from 'js/components/system/App/store/context';

import * as hooks from './hooks';
import * as helpers from './helpers';

import style from './CookiesRegulationMessage.scss';

export const CookiesRegulationMessage = () => {
    const { addLoadingPromise } = useContext(AppContext);
    const role = useUserSelector(prop('role'));
    const dispatch = useDispatch();
    const location = useLocation();
    const [isHidden, setIsHidden] = useState(true);
    const {
        groupsData,
        defaultCookieName,
        storageCookieName,
        cookieExpirationDays,
        isCommonCookieUpdated
    } = useCookieSettingsSelector(pick([
        'groupsData',
        'defaultCookieName',
        'storageCookieName',
        'cookieExpirationDays',
        'isCommonCookieUpdated'
    ]));
    const { cookiesInformationWC, commonCookieName, cookieSettingsPage } = useAppSelector(prop('cookiesConfiguration'));
    const showingMode = hooks.useShowingMode();
    const commonCookie = hooks.useCommonCookie();
    const t = usePortletTranslations();
    const navigate = useNavigate();
    const hideByUrl = getURLParam(location.search, 'hideCookieLayer') === '1';

    const onClose = useCallback(() => {
        setCookie(
            commonCookieName || defaultCookieName,
            1,
            { expires: cookieExpirationDays, path: '/', domain: getBaseDomain() }
        );
        publicEventsAPIService.trigger('cookieMessageClosed');
        dispatch(setCommonCookieFlag(true));
    }, [
        commonCookieName,
        cookieExpirationDays,
        defaultCookieName,
        dispatch
    ]);

    const onAccept = useCallback(() => {
        const acceptedCookiesGroups = helpers.getCookiesGroups(groupsData, true);

        dispatch(setCookiesGroups(acceptedCookiesGroups));
        storageService.local.setItem(storageCookieName, acceptedCookiesGroups);

        setCookie(
            commonCookieName || defaultCookieName,
            1,
            { expires: cookieExpirationDays, path: '/', domain: getBaseDomain() }
        );
        publicEventsAPIService.trigger('cookieMessageClosed');
        dispatch(setCommonCookieFlag(true));
    }, [
        commonCookieName,
        cookieExpirationDays,
        defaultCookieName,
        dispatch,
        groupsData,
        storageCookieName
    ]);

    const onReject = useCallback(() => {
        const rejectedCookiesGroups = helpers.getCookiesGroups(groupsData, false);

        dispatch(setCookiesGroups(rejectedCookiesGroups));
        storageService.local.setItem(storageCookieName, rejectedCookiesGroups);

        setCookie(
            commonCookieName || defaultCookieName,
            1,
            { expires: cookieExpirationDays, path: '/', domain: getBaseDomain() }
        );
        publicEventsAPIService.trigger('cookieMessageClosed');
        dispatch(setCommonCookieFlag(true));
    }, [
        commonCookieName,
        cookieExpirationDays,
        defaultCookieName,
        dispatch,
        groupsData,
        storageCookieName
    ]);

    const onSettings = useCallback(() => {
        navigate(cookieSettingsPage, { prevPath: getCleanUrl() });
    }, [
        cookieSettingsPage,
        navigate
    ]);

    useEffect(() => {
        const isHideMessage = hideByUrl || Number(commonCookie) || isCommonCookieUpdated
            || role === 'admin' || !navigator.cookieEnabled || showingMode === 'hidden';

        if (isHideMessage) {
            setIsHidden(true);

            return;
        }

        setIsHidden(false);
    }, [
        role,
        commonCookie,
        showingMode,
        isCommonCookieUpdated,
        hideByUrl
    ]);

    if (isHidden) {
        return null;
    }

    return (
        <div className={`${style.container} cms-cookies-regulation-message`}>
            <div className={`${style.wc} ${showingMode === 'common' ? style.common : ''}`}>
                <WebContent id={cookiesInformationWC} addLoadingPromise={addLoadingPromise} />
            </div>
            <div className={style.buttons}>
                {showingMode === 'common' && <div className={style.buttonClose} onClick={onClose} />}
                {showingMode === 'persistent' && (
                    <>
                        <Button
                            size={'small'}
                            color={'success'}
                            view={'secondary'}
                            onClick={onAccept}
                            className={`${style.button} ${style.accept}`}
                        >
                            {t('playtech.mobile.system.cookiesRegulation.acceptBtn')}
                        </Button>
                        <Button
                            size={'small'}
                            color={'success'}
                            view={'secondary'}
                            onClick={onReject}
                            className={`${style.button} ${style.reject}`}
                        >
                            {t('playtech.mobile.system.cookiesRegulation.rejectBtn')}
                        </Button>
                    </>
                )}
                {showingMode === 'persistent' && cookieSettingsPage && (
                    <Button
                        size={'small'}
                        color={'success'}
                        view={'secondary'}
                        onClick={onSettings}
                        className={`${style.button} ${style.setup}`}
                    >
                        {t('playtech.mobile.system.cookiesRegulation.setupBtn')}
                    </Button>
                )}
            </div>
        </div>
    );
};

componentService.registerWebContentComponents({ CookiesRegulationMessage });
